import React, { useEffect, useState } from 'react';
import { InfoMessage, Spinner } from 'components';
import styled from 'styled-components/macro';
import {
  Rule,
  RuleRunStatus,
  useGetRuleDetailsAndHistoryQuery,
} from 'types/dt-graphql-apollo-v2';
import { getSchemaTypeForRule, sortRunsByDate } from 'utils/data-validation';
import { Project, Property, Schema, useGetProjectQuery } from 'hooks/projects';
import { Skeleton } from '@cognite/cogs.js';
import { RuleDetailsSegment } from './RuleDetailsSegment';
import { RunInstancesTable } from './RunInstancesTable';

export const ViewReportRulePage = ({
  ruleId,
  schemaCollectionRefId,
}: {
  ruleId: string;
  schemaCollectionRefId: string;
}) => {
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>();

  // Fetch all the runs for this rule
  const {
    data: ruleData,
    loading: ruleLoading,
    error: ruleError,
  } = useGetRuleDetailsAndHistoryQuery({
    variables: {
      ruleId,
    },
  });

  // Get the schema information from FDM
  const {
    data: projectData,
    loading: projectLoading,
    error: projectError,
  } = useGetProjectQuery(schemaCollectionRefId);
  const project = projectData?.Project?.[0] as Project;

  const rule = ruleData?.listRule?.items[0] as Rule;
  const sortedRuns = sortRunsByDate(
    (rule?.ruleRunStatus?.items as RuleRunStatus[]) || []
  );

  const selectedSchemaType = getSchemaTypeForRule(rule);

  // Update the current schema information
  useEffect(() => {
    if (project?.schemas && selectedSchemaType) {
      const schema =
        project.schemas.find((s) => s?.name === selectedSchemaType) || null;
      setSelectedSchema(schema as Schema);
    }
  }, [project, selectedSchemaType]);

  const excludeIDFromProperties = (properties: Array<Property>) =>
    properties.filter((prop) => prop?.type !== 'ID!' && prop?.type !== 'ID');

  const renderInstancesContent = () => {
    if (projectLoading || ruleLoading) {
      return (
        <CenteredContainer>
          <Spinner />
        </CenteredContainer>
      );
    }

    if (projectError) {
      return (
        <InfoMessage type="Error" title="Could not fetch the failed instances">
          {projectError?.message}
        </InfoMessage>
      );
    }

    const currentRun = sortedRuns[0];
    if (project && currentRun) {
      return (
        <StyledContent>
          <RunInstancesTable
            project={project}
            selectedRule={rule}
            selectedRun={currentRun}
            selectedSchema={selectedSchema}
            schemaCollectionRefId={schemaCollectionRefId}
            schemaProperties={excludeIDFromProperties(
              selectedSchema?.properties || []
            )}
          />
        </StyledContent>
      );
    }
    return <InfoMessage type="Error" title="No data available" />;
  };

  const renderRuleDetailsContent = () => {
    if (ruleLoading) {
      return <Skeleton.Paragraph lines={10} />;
    }

    if (ruleError) {
      return (
        <InfoMessage type="Error" title="Could not fetch rule details">
          {ruleError?.message}
        </InfoMessage>
      );
    }
    return <RuleDetailsSegment selectedRule={rule} />;
  };

  return (
    <StyledPage>
      <StyledContainer>
        {renderInstancesContent()}
        {renderRuleDetailsContent()}
      </StyledContainer>
    </StyledPage>
  );
};

const StyledPage = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const StyledContainer = styled.div`
  background: var(--cogs-bg-accent);
  display: grid;
  grid-template-columns: 70% 30%;
  gap: 0.5rem;
  flex: 1;
  min-height: 100%;
`;

const StyledContent = styled.div`
  padding: 2rem;
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const CenteredContainer = styled.div`
  min-height: 50vh;
  position: relative;
`;
